import { match } from "ts-pattern";
import type { SnakeCase } from "type-fest";
import type { ZodType } from "zod";

import { OrganisationIdSchema, type OrganisationId } from "./organisation.js";
import { RoleSchema, type Role } from "./user.js";

/**
 * The organisation Thalamos staff hold their administrative membership at.
 *
 * Administration is a membership rather than an exemption, so administrative
 * reach is granted to a `thalamos_admin` role held *here*. Decision:
 * `docs/decisions/2026-09-08-administration-is-a-capacity.md`.
 *
 * Parsed rather than asserted, so a value that is not a UUID fails at module load
 * with a named error instead of flowing into a query as a branded lie.
 *
 * Seeded by `haven-server`'s migration `3_seed-thalamos-organisation.sql`, which
 * repeats the literal. The two must agree; the migration says what a mismatch
 * looks like.
 */
export const THALAMOS_ORGANISATION_ID: OrganisationId =
  OrganisationIdSchema.parse("94c65d95-445d-4d56-92af-4c4d3e0f4200");

/** The role that confers administrative reach when held at the organisation above. */
export const THALAMOS_ADMIN_ROLE = "thalamos_admin" as const;

/**
 * A role that may be granted at a customer organisation.
 *
 * `thalamos_admin` is excluded because the administrative membership is not
 * Haven's to hand out: it follows the Azure AD directory group, which
 * `reconcileAdministration` is the only writer of. Granting it here would
 * confer administrative reach — `isThalamosAdminMembership` asks only for the
 * role and the organisation — with nothing in the directory behind it.
 *
 * Derived from `RoleSchema` so the two role sets cannot silently disagree. The
 * union is written out as well because tsoa cannot follow a `z.infer` into the
 * API contract, and `satisfies` fails if either is changed alone.
 */
export type CustomerRole =
  | "assessor"
  | "responsible_user"
  | "approver"
  | "viewer"
  | "organisation_admin";

export const CustomerRoleSchema = RoleSchema.exclude([
  THALAMOS_ADMIN_ROLE,
]) satisfies ZodType<CustomerRole & SnakeCase<CustomerRole>>;

/**
 * The customer roles among those a session combines. Matched role by role, so a
 * role added later does not compile until it says whether it is a customer's.
 */
export const customerRolesOf = (roles: readonly Role[]): CustomerRole[] =>
  roles.flatMap((role) =>
    match(role)
      .with(
        "assessor",
        "responsible_user",
        "approver",
        "viewer",
        "organisation_admin",
        (customerRole): CustomerRole[] => [customerRole],
      )
      .with(THALAMOS_ADMIN_ROLE, (): CustomerRole[] => [])
      .exhaustive(),
  );
